import { FunctionComponent, memo } from 'react';

import { ComponentContextProps, merge, useComponent } from '@/components';
import { SVGProps } from '@/utils';

export type IconProps = SVGProps & Partial<ComponentContextProps> & { SVGComponent: FunctionComponent<SVGProps> };

const IIcon = ({ SVGComponent, variant, className, ...rest }: IconProps) => {
  const context = useComponent({ variant });

  /* prettier-ignore */
  const styles = [
    'size-4',

    /* Button */
    'group-[*]/button:size-4',

     /* MenuItem */
    'group-[*]/menu-item:size-4',

    /* FilterGroup */
    'group-[*]/filter-group:size-4',

    /* AgentRunsRow */
    'group-[*]/agent-run-row:size-4',

    /* Tab */
    'group-[*]/tab:size-5',
  ];

  /* prettier-ignore */
  const plainStyles = [
    'text-gray-500',

    /* Button */
    'group-[*]/button:text-gray-700',
    'group-data-[disabled]/button:text-gray-300',

     /* MenuItem */
    'group-data-[disabled]/menu-item:text-gray-300',
    'group-data-[selected]/menu-item:text-gray-700',

    /* FilterGroup */
    'group-data-[selected]/filter-group:text-primary-500',
    'group-[*]/filter-group:text-gray-400',
    'group-hover/filter-group:text-gray-600',

    /* AgentRunsRow */
    'group-[*]/agent-run-row:text-gray-600',

    /* Tab */
    'group-[*]/tab:text-gray-800',

    /* Tooltip */
    'group-[*]/tooltip:text-gray-200',
    'hover:group-[*]/tooltip:text-gray-400',
    'focus:group-[*]/tooltip:text-gray-400',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    'text-primary-500',

    /* Button */
    'group-[*]/button:text-white',
    'group-data-[disabled]/button:text-gray-300',

    /* Logo */
    'group-[*]/logo:text-white',
    'group-[*]/logo:min-size-2',
    'group-[*]/logo:m-1',

    /* Tooltip */
    'group-[*]/tooltip:text-gray-200',
  ];

  /* prettier-ignore */
  const secondaryStyles = [
    /* Button */
    'group-[*]/button:text-gray-700',
    'group-data-[disabled]/button:text-gray-300',
  ];

  /* prettier-ignore */
  const tertiaryStyles = [
    'text-white',

    /* Button */
    'group-[*]/button:text-gray-600',
  ];

  /* prettier-ignore */
  const successStyles = [
    'text-success-500',
  ];

  /* prettier-ignore */
  const warningStyles = [
    'text-warning-400',
  ];

  /* prettier-ignore */
  const errorStyles = [
    'text-error-500',
  ];

  /* prettier-ignore */
  const triggerStyles = [
    'text-primary-500',
  ];

  /* prettier-ignore */
  const exportStyles = [
    'text-export-500',
  ];

  /* prettier-ignore */
  const dangerStyles = [
    'text-danger-700',

    'group-[*]/button:text-danger-700',
    'group-data-[disabled]/button:text-gray-500',
    'group-data-[pressed]/button:text-danger-500',
    'group-hover/button:text-danger-500',
  ];

  return (
    <SVGComponent
      className={merge(
        styles,
        context.variant === 'default' && plainStyles,
        context.variant === 'plain' && plainStyles,
        context.variant === 'primary' && primaryStyles,
        context.variant === 'secondary' && secondaryStyles,
        context.variant === 'tertiary' && tertiaryStyles,
        context.variant === 'success' && successStyles,
        context.variant === 'warning' && warningStyles,
        context.variant === 'error' && errorStyles,
        context.variant === 'trigger' && triggerStyles,
        context.variant === 'export' && exportStyles,
        context.variant === 'danger' && dangerStyles,
        className
      )}
      {...rest}
    />
  );
};

export const Icon = memo(IIcon) as typeof IIcon;
